<template>
  <div>
    <main-navigation></main-navigation>

    <div class="sms-history-container">
      
      <!-- HEADER -->
      <div class="d-flex justify-content-between align-items-center mb-4 header-row">
        <div>
          <h2 class="page-title">SMS History</h2>
          <p class="text-muted mb-0">View all sent SMS messages to tenants</p>
        </div>
        
        <router-link to="/tenants/sms" class="btn btn-primary">
          <i class="bi bi-envelope-paper-fill me-2"></i>
          Send New SMS
        </router-link>
      </div>

      <!-- Filters -->
      <div class="card shadow-sm border-0 mb-4">
        <div class="card-body p-3">
          <div class="row g-3">
            <div class="col-md-4">
              <div class="search-wrapper">
                <i class="bi bi-search search-icon"></i>
                <input
                  v-model="search"
                  type="text"
                  class="form-control search-box"
                  placeholder="Search by phone number or message..."
                />
              </div>
            </div>
            <!-- <div class="col-md-3">
              <select v-model="filterDate" class="form-select">
                <option value="">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="week">Last 7 days</option>
                <option value="month">Last 30 days</option>
              </select>
            </div> -->
            <!-- <div class="col-md-3">
              <input type="date" v-model="startDate" class="form-control" placeholder="Start Date">
            </div> -->
            <!-- <div class="col-md-2">
              <button @click="clearFilters" class="btn btn-outline-secondary w-100">
                <i class="bi bi-x-circle"></i> Clear
              </button>
            </div> -->
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-muted mt-2">Loading SMS history...</p>
      </div>

      <!-- Error State -->
      <div v-if="error" class="alert alert-danger">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        {{ error }}
      </div>

      <!-- SMS History Table -->
      <div v-if="!loading && !error && filteredSmsHistory.length > 0" class="dash-card">
          <div class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr>
                  <th style="width: 50px">
                    <input 
                      type="checkbox" 
                      v-model="selectAll"
                      @change="toggleSelectAll"
                      class="form-check-input"
                    >
                  </th>
                  <th>Phone Number</th>
                  <th>Message</th>
                  <th>Sent Time</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sms in filteredSmsHistory" :key="sms.id">
                  <td>
                    <input 
                      type="checkbox" 
                      v-model="selectedSms"
                      :value="sms.id"
                      class="form-check-input"
                    >
                  </td>
                  <td>
                    <span class="phone-number">
                      <i class="bi bi-telephone-fill me-1"></i>
                      {{ formatPhoneNumber(sms.dest_msisdn) }}
                    </span>
                  </td>
                  <td>
                    <div class="message-preview">
                      {{ truncateMessage(sms.message, 100) }}
                      <a v-if="sms.message.length > 100" href="#" @click.prevent="viewFullMessage(sms)" class="text-primary ms-1">
                        Read more
                      </a>
                    </div>
                  </td>
                  <td>
                    <span class="sent-time">
                      <i class="bi bi-clock me-1"></i>
                      {{ formatDate(sms.sent_date) }}
                    </span>
                  </td>
                  <td>
                    <span class="dash-badge" :class="getStatusClass(sms.status)">
                      <i :class="getStatusIcon(sms.status)"></i>
                      {{ sms.status || 'Sent' }}
                    </span>
                  </td>
                  <td>
                    <button 
                      @click="resendSms(sms)" 
                      class="btn btn-sm btn-outline-primary me-1"
                      title="Resend SMS"
                    >
                      <i class="bi bi-arrow-repeat"></i>
                    </button>
                    <button 
                      @click="viewDetails(sms)" 
                      class="btn btn-sm btn-outline-info"
                      title="View Details"
                    >
                      <i class="bi bi-eye"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && !error && filteredSmsHistory.length === 0 && smsHistory.length > 0" class="text-center py-5">
        <i class="bi bi-inbox display-4 d-block mb-3 text-muted"></i>
        <h5 class="text-muted">No matching SMS found</h5>
        <p class="text-muted">Try adjusting your search or filters</p>
      </div>

      <div v-if="!loading && !error && smsHistory.length === 0" class="text-center py-5">
        <i class="bi bi-envelope-paper display-4 d-block mb-3 text-muted"></i>
        <h5 class="text-muted">No SMS History</h5>
        <p class="text-muted">You haven't sent any SMS messages yet</p>
        <router-link to="/tenants/sms" class="btn btn-primary mt-2">
          <i class="bi bi-envelope-paper-fill me-2"></i>
          Send Your First SMS
        </router-link>
      </div>

      <!-- Bulk Actions -->
      <div v-if="selectedSms.length > 0" class="bulk-actions">
        <div class="card shadow-sm">
          <div class="card-body py-2">
            <div class="d-flex justify-content-between align-items-center">
              <span>
                <i class="bi bi-check2-circle text-primary me-2"></i>
                {{ selectedSms.length }} message(s) selected
              </span>
              <div>
                <button @click="deleteSelected" class="btn btn-sm btn-danger me-2">
                  <i class="bi bi-trash"></i> Delete Selected
                </button>
                <button @click="exportSelected" class="btn btn-sm btn-success">
                  <i class="bi bi-download"></i> Export
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="d-flex justify-content-center mt-4">
        <nav>
          <ul class="pagination">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="changePage(currentPage - 1)">Previous</button>
            </li>
            <li class="page-item" v-for="page in displayedPages" :key="page" :class="{ active: page === currentPage }">
              <button class="page-link" @click="changePage(page)">{{ page }}</button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button class="page-link" @click="changePage(currentPage + 1)">Next</button>
            </li>
          </ul>
        </nav>
      </div>

      <!-- Full Message Modal -->
      <div v-if="showMessageModal" class="modal fade show d-block modal-overlay" @click.self="closeModal">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Full Message</h5>
              <button type="button" class="btn-close" @click="closeModal"></button>
            </div>
            <div class="modal-body">
              <p><strong>To:</strong> {{ formatPhoneNumber(selectedSmsDetails?.dest_msisdn) }}</p>
              <p><strong>Sent:</strong> {{ formatDate(selectedSmsDetails?.sent_date) }}</p>
              <hr>
              <p class="full-message">{{ selectedSmsDetails?.message }}</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
              <button type="button" class="btn btn-primary" @click="resendSms(selectedSmsDetails)">
                <i class="bi bi-arrow-repeat"></i> Resend
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";

export default {
  name: "SmsHistory",
  
  data() {
    return {
      smsHistory: [],
      loading: false,
      error: null,
      search: "",
      filterDate: "",
      startDate: "",
      endDate: "",
      selectedSms: [],
      selectAll: false,
      currentPage: 1,
      itemsPerPage: 20,
      showMessageModal: false,
      selectedSmsDetails: null
    };
  },

  computed: {
    filteredSmsHistory() {
      let filtered = [...this.smsHistory];
      
      // Search filter
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(sms => 
          sms.dest_msisdn?.toLowerCase().includes(searchLower) ||
          sms.message?.toLowerCase().includes(searchLower)
        );
      }
      
      // Date filters
      if (this.filterDate || this.startDate) {
        const now = new Date();
        let start = null;
        
        switch (this.filterDate) {
          case 'today':
            start = new Date(now.setHours(0, 0, 0, 0));
            break;
          case 'yesterday':
            start = new Date(now.setDate(now.getDate() - 1));
            start.setHours(0, 0, 0, 0);
            break;
          case 'week':
            start = new Date(now.setDate(now.getDate() - 7));
            break;
          case 'month':
            start = new Date(now.setDate(now.getDate() - 30));
            break;
        }
        
        if (this.startDate) {
          start = new Date(this.startDate);
        }
        
        if (start) {
          filtered = filtered.filter(sms => new Date(sms.sent_at) >= start);
        }
      }
      
      return filtered;
    },
    
    totalPages() {
      return Math.ceil(this.filteredSmsHistory.length / this.itemsPerPage);
    },
    
    displayedPages() {
      const pages = [];
      const maxPages = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
      let end = Math.min(this.totalPages, start + maxPages - 1);
      
      if (end - start + 1 < maxPages) {
        start = Math.max(1, end - maxPages + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },

  watch: {
    selectedSms: {
      handler(val) {
        this.selectAll = val.length === this.filteredSmsHistory.length && this.filteredSmsHistory.length > 0;
      },
      deep: true
    },
    
    filteredSmsHistory() {
      this.selectedSms = [];
      this.selectAll = false;
      this.currentPage = 1;
    }
  },

  methods: {
    async loadSmsHistory() {
      this.loading = true;
      this.error = null;
      
      try {
        const res = await apiFetch("/sms-history");
        console.log("SMS History Response:", res);
        
        if (res.success !== false) {
          this.smsHistory = res.data || res || [];
        } else {
          this.smsHistory = [];
          this.error = res.message || "Failed to load SMS history";
        }
        
        console.log(`Loaded ${this.smsHistory.length} SMS records`);
      } catch (err) {
        console.error("Failed to load SMS history:", err);
        this.error = err.message || "Failed to load SMS history";
        this.smsHistory = [];
      } finally {
        this.loading = false;
      }
    },
    
    formatPhoneNumber(phone) {
      if (!phone) return "N/A";
      // Format as 254 XXX XXX XXX
      const cleaned = phone.toString().replace(/\D/g, '');
      if (cleaned.length === 12) {
        return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9, 12)}`;
      }
      return phone;
    },
    
    formatDate(date) {
      if (!date) return "N/A";
      const d = new Date(date);
      return d.toLocaleString('en-KE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    truncateMessage(message, length) {
      if (!message) return "";
      if (message.length <= length) return message;
      return message.substring(0, length) + "...";
    },
    
    getStatusClass(status) {
      switch(status?.toLowerCase()) {
        case 'sent': return 'dash-badge--success';
        case 'delivered': return 'dash-badge--primary';
        case 'failed': return 'dash-badge--danger';
        default: return '';
      }
    },
    
    getStatusIcon(status) {
      switch(status?.toLowerCase()) {
        case 'sent': return 'bi bi-check-circle';
        case 'delivered': return 'bi bi-check2-circle';
        case 'failed': return 'bi bi-x-circle';
        default: return 'bi bi-clock';
      }
    },
    
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedSms = this.filteredSmsHistory.map(sms => sms.id);
      } else {
        this.selectedSms = [];
      }
    },
    
    clearFilters() {
      this.search = "";
      this.filterDate = "";
      this.startDate = "";
      this.selectedSms = [];
      this.currentPage = 1;
    },
    
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.selectedSms = [];
        this.selectAll = false;
      }
    },
    
    viewFullMessage(sms) {
      this.selectedSmsDetails = sms;
      this.showMessageModal = true;
    },
    
    closeModal() {
      this.showMessageModal = false;
      this.selectedSmsDetails = null;
    },
    
    async resendSms(sms) {
      if (!confirm(`Resend SMS to ${sms.dest_msisdn}?`)) return;
      
      try {
        const res = await apiFetch("/tenant-sms", {
          method: "POST",
          body: JSON.stringify({
            recipients: [{
              id: sms.tenant_id,
              phone: sms.dest_msisdn,
              name: sms.tenant_name || "Tenant"
            }],
            message: sms.message,
            total_recipients: 1
          })
        });
        
        if (res.success !== false) {
          alert("SMS resent successfully!");
          this.loadSmsHistory(); // Refresh history
        } else {
          throw new Error(res.message || "Failed to resend SMS");
        }
      } catch (err) {
        console.error("Resend failed:", err);
        alert("Failed to resend SMS: " + err.message);
      }
    },
    
    async deleteSelected() {
      if (this.selectedSms.length === 0) return;
      
      if (!confirm(`Delete ${this.selectedSms.length} message(s)? This action cannot be undone.`)) return;
      
      try {
        // Delete each selected SMS
        for (const id of this.selectedSms) {
          await apiFetch(`/sms/${id}`, { method: "DELETE" });
        }
        
        alert(`${this.selectedSms.length} message(s) deleted successfully`);
        this.selectedSms = [];
        this.loadSmsHistory();
      } catch (err) {
        console.error("Delete failed:", err);
        alert("Failed to delete messages: " + err.message);
      }
    },
    
    exportSelected() {
      const selectedData = this.smsHistory.filter(sms => this.selectedSms.includes(sms.id));
      const csv = this.convertToCSV(selectedData);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sms_export_${new Date().toISOString().slice(0,19)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    
    convertToCSV(data) {
      const headers = ['Phone Number', 'Message', 'Sent Time', 'Status'];
      const rows = data.map(sms => [
        sms.dest_msisdn,
        `"${sms.message.replace(/"/g, '""')}"`,
        this.formatDate(sms.sent_at),
        sms.status || 'Sent'
      ]);
      return [headers, ...rows].map(row => row.join(',')).join('\n');
    },
    
    viewDetails(sms) {
      this.viewFullMessage(sms);
    }
  },

  mounted() {
    this.loadSmsHistory();
  }
};
</script>

<style scoped>
.sms-history-container {
  padding: 30px 40px;
  background: var(--dash-bg);
  min-height: calc(100vh - 80px);
}

.page-title {
  font-weight: 700;
  color: var(--dash-primary);
  margin: 0;
}

.header-row {
  flex-wrap: wrap;
  gap: 15px;
}

/* Search */
.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--dash-faint);
  z-index: 10;
}

.search-box {
  width: 100%;
  padding-left: 40px;
  border-radius: var(--dash-pill);
  border: 1px solid var(--dash-line);
  background: white;
  transition: all 0.2s;
}

.search-box:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  border-color: var(--dash-primary);
}

/* Table */
.table {
  margin-bottom: 0;
}

.table th {
  font-size: 14px;
  font-weight: 600;
  color: var(--dash-muted);
  padding: 16px 12px;
  border-bottom-width: 1px;
}

.table td {
  font-size: 14px;
  padding: 16px 12px;
  vertical-align: middle;
}

.phone-number {
  font-weight: 500;
  font-family: var(--dash-font-mono);
  color: var(--dash-ink);
}

.message-preview {
  max-width: 400px;
  color: var(--dash-text);
  line-height: 1.4;
}

.sent-time {
  font-size: 13px;
  color: var(--dash-muted);
  white-space: nowrap;
}

/* Bulk Actions */
.bulk-actions {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  min-width: 400px;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateX(-50%) translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
}

/* Modal */
.modal-overlay {
  background: rgba(0, 0, 0, 0.6);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-dialog {
  max-width: 600px;
  width: 90%;
  margin: auto;
}

.modal-content {
  border-radius: var(--dash-radius-lg);
}

.full-message {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.6;
  background: var(--dash-surface-2);
  padding: 15px;
  border-radius: var(--dash-radius-xs);
  margin: 0;
}

/* Pagination */
.pagination {
  margin-bottom: 0;
}

.page-link {
  cursor: pointer;
  color: var(--dash-primary);
  border: 1px solid var(--dash-line);
  padding: 8px 12px;
}

.page-link:hover {
  background: var(--dash-bg);
  border-color: var(--dash-primary);
}

.page-item.active .page-link {
  background: var(--dash-primary);
  border-color: var(--dash-primary);
  color: white;
}

.page-item.disabled .page-link {
  color: var(--dash-faint);
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .sms-history-container {
    padding: 20px;
  }
  
  .bulk-actions {
    left: 20px;
    right: 20px;
    transform: none;
    min-width: auto;
  }
  
  .bulk-actions .card-body {
    padding: 12px;
  }
  
  .table {
    font-size: 13px;
  }
  
  .table td, .table th {
    padding: 12px 8px;
  }
  
  .message-preview {
    max-width: 200px;
  }
}
</style>